/**
 * Il calendario dei ritiri — la pagina di Marina (#39).
 *
 * Non è una dashboard con dentro un calendario: è il tabellone visto dal lato
 * del tempo. Stesse righe, stesso drawer, stessa scrittura — cambia la domanda
 * a cui risponde. Il tabellone dice «chi devo sollecitare»; qui si vede «quando
 * esce cosa», che è la domanda che nessuna tabella sa mostrare.
 *
 * Due viste (decisione di Enrico, 13-08):
 *  - **settimana**, quella di partenza: lun–sab a piena altezza, la giornata
 *    divisa in mattina e pomeriggio, perché il 21 luglio ha 7 carichi;
 *  - **mese**: giorni quadrati, niente mezze giornate, una riga per carico.
 *    Oltre le quattro compare «+N altri», che apre il **drawer del giorno**.
 *
 * Il gesto centrale: **trascinare una riga su un giorno scrive la data** (fonte
 * Marina, che batte NDP, M1 e Aida). Sul **passato** il gesto cambia significato
 * — vuol dire «è uscito allora» — quindi si chiede conferma invece di dare per
 * scontato.
 */

import {
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Eye,
  HelpCircle,
  Loader2,
  Search,
  Truck,
  UserX,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { TabModulo } from "@/components/TabModulo";
import { SEZIONI_M3 } from "@/lib/nav-config";
import { CaricoPrevistoDrawer } from "@/components/m3/CaricoPrevistoDrawer";
import { PacchettoDrawer } from "@/components/m3/PacchettoDrawer";
import { cercaRighe, STATO_LABEL } from "@/components/m3/stato-carico";
import { Skeleton } from "@/components/ui/skeleton";
import { useCalendario, useCpPatchRiga, useCpUscitaRiga } from "@/hooks/useM3";
import { useVediComeStato } from "@/hooks/useVediCome";
import { cn } from "@/lib/utils";
import type { CalendarioGiorno, CaricoPrevistoRow, FasciaRitiro } from "@/types/api";

type Vista = "settimana" | "mese";

const DOW = ["lun", "mar", "mer", "gio", "ven", "sab", "dom"];
const MESI = [
  "gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
  "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre",
];
/** Quante carte entrano in una cella del mese, misurate a schermo.
 *
 * Era `MAX_MESE = 4` fisso, scelto perché «quattro coprono l'80% dei giorni».
 * Il numero però non dipende dai dati ma dallo **spazio**: la cella è
 * `aspect-square`, quindi la sua altezza è la sua larghezza, e la larghezza è
 * quella dello schermo diviso sette. Misurato il 29-08: a 1920px ci stanno 6
 * carte, a 1600px 4, **a 1280px solo 2** — dove il tetto a quattro ne infilava
 * due dentro `overflow-hidden`, tagliate a metà e senza nemmeno il «+N altri»
 * a dire che c'erano.
 *
 * Le costanti sono le altezze reali dei pezzi (carta 21px + 2 di gap, testa 17,
 * «+N altri» 15, padding 8): si preventiva sempre il «+N altri», così quando
 * c'è non sfonda.
 */
const H_CARTA = 23;
const H_TESTA = 17;
const H_ALTRI = 15;
const H_PADDING = 8;

function carteCheEntrano(larghezzaGriglia: number): number {
  const lato = larghezzaGriglia / 7; // aspect-square: altezza = larghezza
  return Math.max(1, Math.floor((lato - H_PADDING - H_TESTA - H_ALTRI) / H_CARTA));
}

function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}
function daIso(s: string): Date {
  const [a, m, g] = s.split("-").map(Number);
  return new Date(a, m - 1, g);
}
function lunedi(d: Date): Date {
  const x = new Date(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  x.setHours(0, 0, 0, 0);
  return x;
}
function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
function etichettaGiorno(s: string): string {
  const d = daIso(s);
  return `${DOW[(d.getDay() + 6) % 7]} ${d.getDate()} ${MESI[d.getMonth()]}`;
}
function righeDelGiorno(g: CalendarioGiorno): CaricoPrevistoRow[] {
  return [...g.senza_fascia, ...g.mattina, ...g.pomeriggio, ...g.uscite];
}

type Bersaglio = { data: string; fascia: FasciaRitiro | null } | "fascia-laterale";
/** Cosa il rilascio deve chiedere prima di scrivere. */
type Domanda =
  | { tipo: "mezza-giornata"; riga: CaricoPrevistoRow; data: string }
  | { tipo: "passato"; riga: CaricoPrevistoRow; data: string };

export function CalendarioPage() {
  const [vista, setVista] = useState<Vista>("settimana");
  const [ancora, setAncora] = useState(() => new Date());
  const [apertoId, setApertoId] = useState<string | null>(null);
  // Il pacchetto documenti aperto sopra la riga (null = chiuso).
  const [plicoId, setPlicoId] = useState<string | null>(null);
  const [giornoAperto, setGiornoAperto] = useState<string | null>(null);
  const [trascinata, setTrascinata] = useState<string | null>(null);
  const [sopra, setSopra] = useState<string | null>(null);
  const [esito, setEsito] = useState<string | null>(null);
  const [domanda, setDomanda] = useState<Domanda | null>(null);
  const [ricerca, setRicerca] = useState("");
  // Quali gruppi della fascia sono aperti. Sta QUI e non dentro il gruppo perché
  // non deve azzerarsi quando cambi settimana: la fascia è una coda di lavoro,
  // non un accessorio della vista. Di default «In ritardo», la più grossa e la
  // più urgente (scelta di Enrico, 13-08).
  const [aperti, setAperti] = useState<Set<string>>(() => new Set(["In ritardo"]));
  // La riga in volo sta anche in un ref: `getData()` è vuoto durante il dragover
  // per disegno del protocollo, e in alcuni browser è inaffidabile pure al drop.
  const trascinataRef = useRef<string | null>(null);

  const oggi = iso(new Date());
  const finestra =
    vista === "settimana" ? { da: iso(lunedi(ancora)), giorni: 6 } : mesePieno(ancora);

  const { data, isLoading } = useCalendario(finestra.da, finestra.giorni);
  const patch = useCpPatchRiga();
  const uscita = useCpUscitaRiga();
  const vediCome = useVediComeStato();

  const righePerId = new Map<string, CaricoPrevistoRow>();
  if (data) {
    for (const g of data.giorni) for (const r of righeDelGiorno(g)) righePerId.set(r.id, r);
    for (const r of [
      ...data.da_collocare.ndp_emessa,
      ...data.da_collocare.in_attesa,
      ...data.in_ritardo,
      ...data.da_verificare,
    ]) {
      righePerId.set(r.id, r);
    }
  }

  function scorri(passi: number) {
    const d = new Date(ancora);
    if (vista === "settimana") d.setDate(d.getDate() + passi * 7);
    else d.setMonth(d.getMonth() + passi);
    setAncora(d);
  }

  function scrivi(riga: CaricoPrevistoRow, data: string | null, fascia: FasciaRitiro | null) {
    patch.mutate(
      { id: riga.id, body: { data_ritiro: data, fascia_ritiro: fascia } },
      {
        onSuccess: () =>
          setEsito(
            data
              ? `Ritiro di ${riga.cliente_nome}: ${etichettaGiorno(data)}${fascia ? ` ${fascia}` : ""} · scritto da te`
              : `${riga.cliente_nome}: data tolta, torna fra quelle da collocare`,
          ),
      },
    );
  }

  function rilascia(bersaglio: Bersaglio, riga: CaricoPrevistoRow) {
    setSopra(null);
    setTrascinata(null);
    trascinataRef.current = null;

    if (bersaglio === "fascia-laterale") {
      if (riga.data_ritiro) scrivi(riga, null, null);
      return;
    }
    const { data, fascia } = bersaglio;
    // Il passato non è un giorno come gli altri: posarci un carico vorrebbe dire
    // che è uscito allora. Non si dà per scontato — si chiede.
    if (data < oggi) return setDomanda({ tipo: "passato", riga, data });
    // Nel mese la zona di rilascio non dice la mezza giornata: la si chiede.
    if (vista === "mese") return setDomanda({ tipo: "mezza-giornata", riga, data });
    if (riga.data_ritiro === data && (riga.fascia_ritiro ?? null) === fascia) return;
    scrivi(riga, data, fascia);
  }

  function consentiRilascio(chiave: string) {
    return (e: React.DragEvent) => {
      e.preventDefault();
      // Senza `dropEffect` il browser calcola «nessun effetto» e RIFIUTA il
      // rilascio in silenzio: la carta torna indietro e sembra un difetto.
      e.dataTransfer.dropEffect = "move";
      if (sopra !== chiave) setSopra(chiave);
    };
  }

  function onDrop(bersaglio: Bersaglio) {
    return (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const id = trascinataRef.current || e.dataTransfer.getData("text/plain");
      const riga = id ? righePerId.get(id) : undefined;
      if (riga) rilascia(bersaglio, riga);
    };
  }

  const dnd: Dnd = {
    sopra,
    trascinata,
    consentiRilascio,
    onDrop,
    onSopra: setSopra,
    onInizio: (id) => {
      trascinataRef.current = id;
      setTrascinata(id);
    },
    onFine: () => {
      trascinataRef.current = null;
      setTrascinata(null);
      setSopra(null);
    },
    onApri: setApertoId,
    onUscita: (id) => uscita.mutate({ id }),
    oggi,
  };

  function apriChiudi(titolo: string) {
    setAperti((s) => {
      const n = new Set(s);
      if (n.has(titolo)) n.delete(titolo);
      else n.add(titolo);
      return n;
    });
  }

  const c = data?.conteggi;
  const giorni = data?.giorni ?? [];
  const dettaglioGiorno = giorni.find((g) => g.data === giornoAperto) ?? null;

  return (
    <div className="mx-auto max-w-[1700px] px-5 pb-14 pt-5">
      {/* Il Calendario è una sezione di Spedizioni con una rotta propria: la fila
          delle tab è la stessa del modulo, così da qui si va nelle altre quattro
          senza passare dalla barra. Il titolo è uscito il 27-08: lo dice la
          pillola accesa, e prima erano due (più quello dell'header). */}
      <TabModulo sezioni={SEZIONI_M3} modulo="M3" />

      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm text-muted-foreground">
            {c ? (
              <>
                <b className="font-semibold text-foreground">{c.da_collocare}</b> da collocare ·{" "}
                <b className="font-semibold text-foreground">{c.in_ritardo}</b> in ritardo
                {c.uscite > 0 && (
                  <>
                    {" · "}
                    <b className="font-semibold text-foreground">{c.uscite}</b> usciti
                  </>
                )}
                {c.oltre > 0 && ` · ${c.oltre} oltre la finestra`}
              </>
            ) : (
              "…"
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex gap-0.5 rounded-lg border bg-muted/40 p-0.5">
            {(["settimana", "mese"] as Vista[]).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setVista(v)}
                aria-pressed={vista === v}
                className={cn(
                  "rounded-md px-3 py-1 text-sm font-medium capitalize transition-colors",
                  vista === v
                    ? "bg-brand-700 text-white"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {v}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => scorri(-1)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border hover:bg-muted"
            aria-label={vista === "settimana" ? "Settimana precedente" : "Mese precedente"}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="min-w-[9.5rem] text-center text-sm font-semibold">
            {titoloPeriodo(vista, ancora, giorni)}
          </span>
          <button
            type="button"
            onClick={() => scorri(1)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border hover:bg-muted"
            aria-label={vista === "settimana" ? "Settimana successiva" : "Mese successivo"}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setAncora(new Date())}
            className="h-8 rounded-md border px-3 text-sm font-medium hover:bg-muted"
          >
            Oggi
          </button>
        </div>
      </header>

      {vediCome && (
        <p className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <Eye className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Stai guardando come <b>{vediCome.label}</b>, e «Vedi come» è in <b>sola lettura</b>:
            puoi trascinare, ma il server rifiuta la scrittura e la riga torna al suo posto.
          </span>
        </p>
      )}
      {(patch.isPending || uscita.isPending) && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> salvo…
        </p>
      )}
      {esito && !patch.isPending && !patch.isError && !uscita.isError && (
        <p className="mt-2 text-xs font-medium text-emerald-700">{esito}</p>
      )}
      {(patch.isError || uscita.isError) && (
        <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
          Non ho salvato:{" "}
          {(patch.error as Error | null)?.message ??
            (uscita.error as Error | null)?.message ??
            "errore sconosciuto"}
        </p>
      )}

      {/* Due colonne, due scorrimenti: sorgente e bersaglio del trascinamento
          devono stare a schermo INSIEME, altrimenti scorrere per cercare una
          riga porta via il posto dove metterla. */}
      <div
        className={cn(
          "mt-4 grid items-start gap-4",
          vista === "settimana"
            ? "lg:grid-cols-[280px_minmax(0,1fr)]"
            : "lg:grid-cols-[240px_minmax(0,1fr)]",
        )}
      >
        <aside
          className="flex flex-col gap-3 lg:sticky lg:top-[4.5rem] lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:pr-1"
          onDragOver={consentiRilascio("tray")}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) setSopra(null);
          }}
          onDrop={onDrop("fascia-laterale")}
          data-zona="fascia"
        >
          {/* `shrink-0` ovunque nella fascia: è un flex column con altezza
              massima, e senza questo i figli si COMPRIMONO invece di sforare —
              il gruppo aperto viene tagliato, gli altri si riducono a niente
              (nomi invisibili, non cliccabili) e non c'è nulla da scorrere,
              quindi scorre la pagina. Difetto trovato da Enrico, 13-08. */}
          <div className="sticky top-0 z-10 shrink-0 bg-background pb-1">
            <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <input
              value={ricerca}
              onChange={(e) => setRicerca(e.target.value)}
              placeholder="Cerca cliente, commessa, NDP…"
                className="h-9 w-full rounded-lg border bg-card pl-8 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              {ricerca && (
                <button
                  type="button"
                  onClick={() => setRicerca("")}
                  aria-label="Pulisci la ricerca"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {isLoading || !data ? (
            <Skeleton className="h-72 w-full shrink-0" />
          ) : (
            <>
              {[
                {
                  titolo: "In ritardo",
                  righe: data.in_ritardo,
                  tono: "rosso" as const,
                  nota:
                    c && c.in_ritardo_da_confermare > 0
                      ? `${c.in_ritardo_da_confermare} hanno la NDP: trascinale sul giorno in cui sono partite`
                      : "la data è passata e la merce non è uscita",
                },
                {
                  titolo: "NDP emessa, manca la data",
                  righe: data.da_collocare.ndp_emessa,
                  nota: "il documento c'è: il camion è imminente ma non sappiamo quando",
                },
                {
                  titolo: "In attesa di risposta",
                  righe: data.da_collocare.in_attesa,
                  nota: "il cliente non ha ancora detto quando ritira",
                },
                {
                  titolo: "Data da verificare",
                  righe: data.da_verificare,
                  tono: "ambra" as const,
                  nota: "la data letta è precedente all'avviso: quasi certamente un anno sbagliato",
                },
              ]
                .filter((g) => g.righe.length > 0)
                .map((g) => (
                  <Gruppo
                    key={g.titolo}
                    titolo={g.titolo}
                    nota={g.nota}
                    tono={g.tono}
                    righe={g.righe}
                    ricerca={ricerca}
                    // Mentre cerchi i gruppi si aprono da soli: un risultato
                    // nascosto dentro un contenitore chiuso è un risultato che
                    // non hai trovato.
                    aperto={!!ricerca || aperti.has(g.titolo)}
                    onApriChiudi={() => apriChiudi(g.titolo)}
                    dnd={dnd}
                  />
                ))}
              {sopra === "tray" && trascinata && (
                <p className="shrink-0 rounded-md border border-dashed p-2 text-center text-xs text-muted-foreground">
                  rilascia qui per togliere la data
                </p>
              )}
            </>
          )}
        </aside>

        <div className="min-w-0">
          {isLoading || !data ? (
            <Skeleton className="h-[32rem] w-full" />
          ) : vista === "settimana" ? (
            <VistaSettimana giorni={giorni} dnd={dnd} onApriGiorno={setGiornoAperto} />
          ) : (
            <VistaMese
              giorni={giorni}
              mese={ancora.getMonth()}
              dnd={dnd}
              onApriGiorno={setGiornoAperto}
            />
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            Trascina una riga su un giorno per fissare il ritiro; riportala a sinistra per togliere
            la data. Sui giorni passati il gesto vuol dire «è uscito allora», e te lo chiedo prima
            di scriverlo.
          </p>
        </div>
      </div>

      {domanda?.tipo === "mezza-giornata" && (
        <Modale
          titolo={`Quando ritira, ${domanda.riga.cliente_nome}?`}
          testo={`${cap(etichettaGiorno(domanda.data))}. Nel mese la mezza giornata non si vede, quindi te la chiedo qui.`}
          azioni={[
            {
              testo: "Mattina",
              primario: true,
              fai: () => scrivi(domanda.riga, domanda.data, "mattina"),
            },
            {
              testo: "Pomeriggio",
              primario: true,
              fai: () => scrivi(domanda.riga, domanda.data, "pomeriggio"),
            },
            { testo: "Non lo so", fai: () => scrivi(domanda.riga, domanda.data, null) },
          ]}
          onChiudi={() => setDomanda(null)}
        />
      )}

      {domanda?.tipo === "passato" &&
        (domanda.riga.puo_partire ? (
          <Modale
            titolo={`Il camion di ${domanda.riga.cliente_nome} è uscito ${etichettaGiorno(domanda.data)}?`}
            testo="È un giorno passato: posarci un carico vuol dire che la merce è già partita. La riga si chiude e resta come storico, con la data che hai scelto."
            azioni={[
              {
                testo: "Sì, registra l'uscita",
                primario: true,
                fai: () =>
                  uscita.mutate(
                    { id: domanda.riga.id, giorno: domanda.data },
                    {
                      onSuccess: () =>
                        setEsito(
                          `${domanda.riga.cliente_nome} segnata uscita il ${etichettaGiorno(domanda.data)}`,
                        ),
                    },
                  ),
              },
              {
                testo: "No, sposta solo la data prevista",
                fai: () => scrivi(domanda.riga, domanda.data, null),
              },
            ]}
            onChiudi={() => setDomanda(null)}
          />
        ) : (
          <Modale
            titolo={`${domanda.riga.cliente_nome} non ha la NDP`}
            testo="Senza NDP (o DDT) l'uscita non si può registrare: è il documento che accompagna la merce, e senza non abbiamo prova che sia partita."
            avviso={`Posso spostare la data prevista su ${etichettaGiorno(domanda.data)}: la riga resta aperta e in ritardo, che è la verità finché il documento non arriva.`}
            azioni={[
              {
                testo: "Sposta solo la data",
                primario: true,
                fai: () => scrivi(domanda.riga, domanda.data, null),
              },
            ]}
            onChiudi={() => setDomanda(null)}
          />
        ))}

      {dettaglioGiorno && (
        <DrawerGiorno
          giorno={dettaglioGiorno}
          onChiudi={() => setGiornoAperto(null)}
          onApriRiga={(id) => {
            setGiornoAperto(null);
            setApertoId(id);
          }}
        />
      )}

      {apertoId && (
        <CaricoPrevistoDrawer
          id={apertoId}
          onClose={() => setApertoId(null)}
          onApriPlico={(caricoId) => setPlicoId(caricoId)}
        />
      )}

      {/* Il pacchetto documenti si apre sopra la riga anche qui: il drawer è lo
          stesso delle due viste, e questa è la seconda. */}
      {plicoId && (
        <PacchettoDrawer
          caricoId={plicoId}
          onClose={() => setPlicoId(null)}
          etichettaIndietro="Torna al carico"
        />
      )}
    </div>
  );
}

/** La finestra che copre il mese come lo disegna un calendario: dal lunedì
 *  prima del primo giorno alla fine dell'ultima settimana. */
function mesePieno(ancora: Date): { da: string; giorni: number } {
  const primo = new Date(ancora.getFullYear(), ancora.getMonth(), 1);
  const ultimo = new Date(ancora.getFullYear(), ancora.getMonth() + 1, 0);
  const da = lunedi(primo);
  const fine = lunedi(ultimo);
  fine.setDate(fine.getDate() + 6);
  return {
    da: iso(da),
    giorni: Math.round((fine.getTime() - da.getTime()) / 86_400_000) + 1,
  };
}

function titoloPeriodo(vista: Vista, ancora: Date, giorni: CalendarioGiorno[]): string {
  if (vista === "mese") return `${cap(MESI[ancora.getMonth()])} ${ancora.getFullYear()}`;
  if (giorni.length === 0) return "—";
  const a = daIso(giorni[0].data);
  const b = daIso(giorni[giorni.length - 1].data);
  return `${a.getDate()}–${b.getDate()} ${MESI[b.getMonth()]}`;
}

type Dnd = {
  sopra: string | null;
  trascinata: string | null;
  consentiRilascio: (chiave: string) => (e: React.DragEvent) => void;
  onDrop: (b: Bersaglio) => (e: React.DragEvent) => void;
  onSopra: (k: string | null) => void;
  onInizio: (id: string) => void;
  onFine: () => void;
  onApri: (id: string) => void;
  onUscita: (id: string) => void;
  oggi: string;
};

// ── vista settimana ─────────────────────────────────────────────────────────

function VistaSettimana({
  giorni,
  dnd,
  onApriGiorno,
}: {
  giorni: CalendarioGiorno[];
  dnd: Dnd;
  onApriGiorno: (data: string) => void;
}) {
  // La domenica non si mostra nella settimana: nei dati non c'è un solo ritiro.
  const feriali = giorni.filter((g) => daIso(g.data).getDay() !== 0);
  return (
    <div className="grid h-[calc(100vh-13rem)] min-h-[26rem] grid-cols-2 overflow-hidden rounded-xl border bg-card sm:grid-cols-3 lg:grid-cols-6">
      {feriali.map((g) => (
        <ColonnaGiorno key={g.data} giorno={g} dnd={dnd} onApriGiorno={onApriGiorno} />
      ))}
    </div>
  );
}

function ColonnaGiorno({
  giorno,
  dnd,
  onApriGiorno,
}: {
  giorno: CalendarioGiorno;
  dnd: Dnd;
  onApriGiorno: (data: string) => void;
}) {
  const d = daIso(giorno.data);
  const passato = giorno.data < dnd.oggi;
  const eOggi = giorno.data === dnd.oggi;
  const sabato = d.getDay() === 6;

  return (
    <div
      className={cn(
        "flex min-w-0 flex-col border-r last:border-r-0",
        passato && "bg-muted/40",
        sabato && !passato && "bg-rose-50/50",
      )}
    >
      <button
        type="button"
        onClick={() => onApriGiorno(giorno.data)}
        title="Apri la giornata"
        className="flex items-baseline justify-between gap-1 px-2 py-1.5 text-left hover:bg-muted/60"
      >
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {DOW[(d.getDay() + 6) % 7]}
        </span>
        <span
          className={cn(
            "text-sm font-bold tabular-nums",
            eOggi && "rounded bg-brand-700 px-1.5 text-white",
          )}
        >
          {d.getDate()}
        </span>
      </button>

      {(giorno.senza_fascia.length > 0 || (dnd.trascinata && !passato)) && (
        <ZonaRilascio
          chiave={`${giorno.data}-libero`}
          etichetta="orario da definire"
          data={giorno.data}
          fascia={null}
          righe={giorno.senza_fascia}
          dnd={dnd}
          tratteggiata
        />
      )}
      <ZonaRilascio
        chiave={`${giorno.data}-mattina`}
        etichetta="mattina"
        data={giorno.data}
        fascia="mattina"
        righe={giorno.mattina}
        dnd={dnd}
        cresce
      />
      <ZonaRilascio
        chiave={`${giorno.data}-pomeriggio`}
        etichetta="pomeriggio"
        data={giorno.data}
        fascia="pomeriggio"
        righe={giorno.pomeriggio}
        dnd={dnd}
        cresce
      />

      {giorno.uscite.length > 0 && (
        <div className="flex flex-col gap-1 border-t bg-muted/60 p-1.5">
          <span className="text-[9.5px] font-semibold uppercase tracking-wider text-muted-foreground/70">
            usciti
          </span>
          {giorno.uscite.map((r) => (
            <Carta key={r.id} row={r} dnd={dnd} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Una mezza giornata, cioè una zona di rilascio.
 *
 * Sta a livello di modulo e non dentro la colonna: definita dentro, React la
 * rimonterebbe a ogni movimento del mouse durante il trascinamento — il nodo
 * sotto il cursore sparisce e il rilascio non arriva mai al suo gestore. È
 * costato un giro di prova a vuoto (12-08).
 */
function ZonaRilascio({
  chiave,
  etichetta,
  data,
  fascia,
  righe,
  dnd,
  cresce,
  tratteggiata,
}: {
  chiave: string;
  etichetta: string;
  data: string;
  fascia: FasciaRitiro | null;
  righe: CaricoPrevistoRow[];
  dnd: Dnd;
  cresce?: boolean;
  tratteggiata?: boolean;
}) {
  return (
    <div
      data-zona={fascia ?? "libero"}
      data-giorno={data}
      data-passato={data < dnd.oggi}
      onDragOver={dnd.consentiRilascio(chiave)}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) dnd.onSopra(null);
      }}
      onDrop={dnd.onDrop({ data, fascia })}
      className={cn(
        "flex flex-col gap-1 overflow-y-auto border-t p-1.5",
        cresce && "min-h-[3.5rem] flex-1",
        tratteggiata && "border-dashed",
        dnd.sopra === chiave && "bg-brand-50 ring-2 ring-inset ring-brand-400",
      )}
    >
      <span className="text-[9.5px] font-semibold uppercase tracking-wider text-muted-foreground/70">
        {etichetta}
      </span>
      {righe.map((r) => (
        <Carta key={r.id} row={r} dnd={dnd} />
      ))}
    </div>
  );
}

// ── vista mese ──────────────────────────────────────────────────────────────

function VistaMese({
  giorni,
  mese,
  dnd,
  onApriGiorno,
}: {
  giorni: CalendarioGiorno[];
  mese: number;
  dnd: Dnd;
  onApriGiorno: (data: string) => void;
}) {
  // Il tetto si ricalcola quando la finestra cambia: la cella è quadrata, quindi
  // rimpicciolire lo schermo le toglie altezza, non solo larghezza.
  const griglia = useRef<HTMLDivElement>(null);
  const [maxCarte, setMaxCarte] = useState(4);
  useEffect(() => {
    const el = griglia.current;
    if (!el) return;
    const misura = () => setMaxCarte(carteCheEntrano(el.getBoundingClientRect().width));
    misura();
    const ro = new ResizeObserver(misura);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <div className="grid grid-cols-7 border-b bg-muted/30">
        {DOW.map((d) => (
          <div
            key={d}
            className="px-2 py-1.5 text-center text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
          >
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7" ref={griglia}>
        {giorni.map((g) => (
          <CellaMese
            key={g.data}
            giorno={g}
            fuoriMese={daIso(g.data).getMonth() !== mese}
            dnd={dnd}
            onApriGiorno={onApriGiorno}
            maxCarte={maxCarte}
          />
        ))}
      </div>
    </div>
  );
}

function CellaMese({
  giorno,
  maxCarte,
  fuoriMese,
  dnd,
  onApriGiorno,
}: {
  giorno: CalendarioGiorno;
  /** Quante carte entrano davvero nella cella a questa larghezza di schermo. */
  maxCarte: number;
  fuoriMese: boolean;
  dnd: Dnd;
  onApriGiorno: (data: string) => void;
}) {
  const d = daIso(giorno.data);
  const passato = giorno.data < dnd.oggi;
  const eOggi = giorno.data === dnd.oggi;
  // Sabato e domenica: fondo rosato appena accennato, non rosso pieno. In questa
  // pagina il rosso vuol dire «in ritardo», e un giorno chiuso non è un giorno in
  // allarme — il sabato peraltro non è del tutto chiuso (2 ritiri in luglio).
  const festivo = d.getDay() === 0 || d.getDay() === 6;
  // ⚠️ Le uscite PRIME, non ultime (29-08). `righeDelGiorno` le mette in fondo
  // — l'ordine con cui si compila una giornata: prima cosa deve uscire, poi
  // cosa è uscito. Ma la cella del mese ne mostra quattro, quindi in fondo
  // significa **le prime a finire dentro «+N altri»**: sul 28 agosto, con 10
  // righe, sparivano tutte e quattro le uscite. E su un giorno passato «cosa è
  // uscito» è la domanda, non un di più — il resto di quel giorno è lavoro in
  // ritardo, che ha già la sua fascia a sinistra. Nei giorni futuri non cambia
  // niente: uscite non ce ne sono.
  const righe = [...giorno.uscite, ...giorno.senza_fascia, ...giorno.mattina, ...giorno.pomeriggio];
  const mostrate = righe.slice(0, maxCarte);
  const altre = righe.length - mostrate.length;

  return (
    <div
      data-zona="giorno"
      data-giorno={giorno.data}
      data-passato={passato}
      onDragOver={dnd.consentiRilascio(giorno.data)}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) dnd.onSopra(null);
      }}
      onDrop={dnd.onDrop({ data: giorno.data, fascia: null })}
      className={cn(
        "flex aspect-square flex-col gap-0.5 overflow-hidden border-b border-r p-1",
        festivo && "bg-rose-50/60",
        passato && "bg-muted/40",
        fuoriMese && "opacity-45",
        dnd.sopra === giorno.data && "bg-brand-50 ring-2 ring-inset ring-brand-400",
      )}
    >
      <button
        type="button"
        onClick={() => onApriGiorno(giorno.data)}
        title="Apri la giornata"
        className={cn(
          "self-start rounded px-1 text-[11.5px] font-bold tabular-nums hover:bg-muted",
          eOggi && "bg-brand-700 text-white hover:bg-brand-700",
        )}
      >
        {d.getDate()}
      </button>
      {mostrate.map((r) => (
        <Riga key={r.id} row={r} dnd={dnd} />
      ))}
      {altre > 0 && (
        <button
          type="button"
          onClick={() => onApriGiorno(giorno.data)}
          className="self-start px-1 text-[10px] font-medium text-brand-700 hover:underline"
        >
          +{altre} altri
        </button>
      )}
    </div>
  );
}

/** La riga del mese: bandiera, nome, e il bordo sinistro a dire lo stato. Due
 *  informazioni, non tre: nello spazio di una riga non ce ne stanno di più. */
function Riga({ row, dnd }: { row: CaricoPrevistoRow; dnd: Dnd }) {
  return (
    <div
      draggable
      role="button"
      tabIndex={0}
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", row.id);
        e.dataTransfer.effectAllowed = "move";
        dnd.onInizio(row.id);
      }}
      onDragEnd={dnd.onFine}
      onClick={() => dnd.onApri(row.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          dnd.onApri(row.id);
        }
      }}
      title={`${row.cliente_nome} — ${STATO_LABEL[row.stato]}${row.ndp_numero ? ` · NDP ${row.ndp_numero}` : ""}${
        row.ddt_numero ? ` · DDT ${row.ddt_numero}` : ""
      }`}
      className={cn(
        "flex w-full min-w-0 cursor-grab items-center gap-1 rounded border border-l-[3px] bg-card px-1 py-px text-[10.5px]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400",
        bordoStato(row),
        // Una riga uscita era `opacity-60`, cioè *meno* leggibile delle altre:
        // giusto quando il calendario serviva a pianificare, sbagliato appena
        // gli si chiede anche «cosa è uscito». Ora ha un fondo verde tenue —
        // si distingue senza gridare, e resta leggibile (richiesta di Enrico).
        row.partito_il && "border-emerald-200 bg-emerald-50 text-emerald-900",
      )}
    >
      <Bandiera row={row} />
      <span className="min-w-0 flex-1 truncate font-semibold">{row.cliente_nome}</span>
      {row.partito_il && <Check className="h-2.5 w-2.5 shrink-0 text-emerald-600" />}
    </div>
  );
}

function bordoStato(row: CaricoPrevistoRow): string {
  if (row.partito_il) return "border-l-emerald-600";
  if (row.cliente_ignoto || row.match_incerto) return "border-l-violet-400";
  if (row.ndp_numero) return "border-l-brand-700";
  if (row.stato === "sollecito_dovuto") return "border-l-red-500";
  if (row.stato === "confermato") return "border-l-emerald-500";
  return "border-l-amber-500";
}

function Bandiera({ row }: { row: CaricoPrevistoRow }) {
  if (!row.paese_iso2) {
    return <span className="h-2.5 w-3.5 shrink-0 rounded-[2px] border border-dashed" />;
  }
  const emoji = String.fromCodePoint(
    ...[...row.paese_iso2.toUpperCase()].map((ch) => 0x1f1a5 + ch.charCodeAt(0)),
  );
  return (
    <span className="shrink-0 text-[11px]" title={row.paese_nome ?? ""}>
      {emoji}
    </span>
  );
}

// ── la carta (settimana e fascia) ───────────────────────────────────────────

/** Perché questa riga è fra i risultati, quando non è per il nome.
 *
 *  Cercando «fidrik» compare anche BISHKEK, che ce l'ha fra i riferimenti del
 *  cliente: è il comportamento voluto (è la ragione per cui la ricerca guarda
 *  anche i riferimenti), ma senza dirlo sembra un risultato sbagliato. */
function perche(row: CaricoPrevistoRow, q: string): string | null {
  const t = q.trim().toLowerCase();
  if (!t || row.cliente_nome.toLowerCase().includes(t)) return null;
  const rif = row.riferimenti_cliente.find((r) => r.toLowerCase().includes(t));
  if (rif) return `rif. ${rif}`;
  const commessa = row.commesse.find((c) => String(c).includes(t));
  if (commessa != null) return `commessa ${commessa}`;
  if (row.ndp_numero?.toLowerCase().includes(t)) return `NDP ${row.ndp_numero}`;
  return null;
}

function Carta({ row, dnd, motivo }: { row: CaricoPrevistoRow; dnd: Dnd; motivo?: string | null }) {
  const senzaNome = row.cliente_ignoto || row.match_incerto;
  const uscito = !!row.partito_il;
  return (
    <div
      draggable
      role="button"
      tabIndex={0}
      onDragStart={(e) => {
        // `setData` serve a Firefox per avviare il trascinamento, anche se poi
        // l'id lo leggiamo dal ref.
        e.dataTransfer.setData("text/plain", row.id);
        e.dataTransfer.effectAllowed = "move";
        dnd.onInizio(row.id);
      }}
      onDragEnd={dnd.onFine}
      onClick={() => dnd.onApri(row.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          dnd.onApri(row.id);
        }
      }}
      title={`${row.cliente_nome} — trascina per fissare il ritiro, clicca per aprire`}
      className={cn(
        "cursor-grab rounded-md border border-l-[3px] bg-card p-1.5 text-left shadow-sm active:cursor-grabbing",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400",
        bordoStato(row),
        // Stesso trattamento della cella del mese: fondo verde invece
        // dell'opacità. Sbiadire ciò che è uscito ha senso in un calendario che
        // pianifica; qui il passato risponde a «cosa è uscito», e quella è la
        // riga da vedere per prima, non da attenuare.
        uscito && "border-emerald-200 bg-emerald-50",
      )}
    >
      <div className="flex w-full min-w-0 items-center gap-1">
        <Bandiera row={row} />
        <span className="min-w-0 flex-1 truncate text-[12px] font-semibold">
          {row.cliente_nome}
        </span>
        {uscito && <Check className="h-3 w-3 shrink-0 text-emerald-600" />}
      </div>

      <div className="mt-1 flex flex-wrap items-center gap-1">
        {uscito ? (
          <>
            <Segnale tono="verde">uscito</Segnale>
            {row.ddt_numero && <Segnale tono="verde">DDT {row.ddt_numero}</Segnale>}
          </>
        ) : (
          <>
            {senzaNome && (
              <Segnale tono="violetto" icona={UserX}>
                chi è?
              </Segnale>
            )}
            {row.domanda && (
              <Segnale tono="ambra" icona={HelpCircle}>
                {row.domanda.campi[0]?.label ?? "domanda"}
              </Segnale>
            )}
            {row.ndp_numero && <Segnale tono="verde">NDP {row.ndp_numero}</Segnale>}
            {/* Un DDT su un carico non ancora uscito è di oggi: il camion sta
                caricando, domani la riga parte da sola (06-10-2026). */}
            {row.ddt_numero && <Segnale tono="ambra">DDT {row.ddt_numero}</Segnale>}
            {!row.ndp_numero && !senzaNome && (
              <Segnale tono="neutro">{STATO_LABEL[row.stato]}</Segnale>
            )}
          </>
        )}
        {row.pallet_previsti != null && (
          <span className="text-[10px] tabular-nums text-muted-foreground">
            {Math.round(row.pallet_previsti)} pallet
          </span>
        )}
      </div>

      {motivo && (
        <p className="mt-0.5 truncate text-[10px] italic text-muted-foreground">{motivo}</p>
      )}

      {row.puo_confermare_uscita && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            dnd.onUscita(row.id);
          }}
          className="mt-1 inline-flex w-full items-center justify-center gap-1 rounded border border-brand-200 bg-brand-50 py-0.5 text-[10.5px] font-semibold text-brand-700 hover:bg-brand-100"
        >
          <Truck className="h-3 w-3" /> è uscito
        </button>
      )}
    </div>
  );
}

function Segnale({
  tono,
  icona: Icona,
  children,
}: {
  tono: "violetto" | "ambra" | "verde" | "neutro";
  icona?: LucideIcon;
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 rounded px-1 py-px text-[10px] font-semibold",
        tono === "violetto" && "bg-violet-100 text-violet-700",
        tono === "ambra" && "bg-amber-100 text-amber-800",
        tono === "verde" && "bg-emerald-100 text-emerald-700",
        tono === "neutro" && "bg-muted text-muted-foreground",
      )}
    >
      {Icona && <Icona className="h-2.5 w-2.5" />}
      {children}
    </span>
  );
}

/**
 * Un contenitore della fascia: la coda di lavoro con il suo perché.
 *
 * Richiudibile perché **la fascia contiene 120 righe su 121**, cioè tutto il
 * tabellone: aperti tutti insieme non si trascina niente, e mostrandone sei per
 * gruppo (com'era) si nasconde il 95% del contenuto — che è esattamente il
 * difetto segnalato da Enrico il 13-08. Aperto = tutte le righe, con lo
 * scorrimento della colonna: uno scroll dentro ogni gruppo litigherebbe col
 * trascinamento sul bordo.
 */
function Gruppo({
  titolo,
  nota,
  righe,
  tono,
  ricerca,
  aperto,
  onApriChiudi,
  dnd,
}: {
  titolo: string;
  nota: string;
  righe: CaricoPrevistoRow[];
  tono?: "rosso" | "ambra";
  ricerca: string;
  aperto: boolean;
  onApriChiudi: () => void;
  dnd: Dnd;
}) {
  const trovate = cercaRighe(righe, ricerca);
  // Durante una ricerca un gruppo senza risultati non si mostra: la fascia deve
  // dire dove SONO le righe cercate, non dove non sono.
  if (ricerca && trovate.length === 0) return null;

  return (
    <div
      className={cn(
        "shrink-0 rounded-xl border",
        tono === "rosso" && "border-red-200 bg-red-50/50",
        tono === "ambra" && "border-amber-200 bg-amber-50/50",
        !tono && "bg-muted/30",
      )}
    >
      {/* Intestazione appiccicata: con «In ritardo» aperto (57 righe) gli altri
          contenitori finiscono sotto un muro di carte, e per arrivarci si
          scorrerebbe tutto. Così restano sempre a portata. Niente
          `overflow-hidden` sul contenitore, altrimenti `sticky` non ha uno
          scrollport a cui agganciarsi; lo sfondo dev'essere PIENO, o le carte si
          vedrebbero passare sotto il titolo. */}
      <button
        type="button"
        onClick={onApriChiudi}
        aria-expanded={aperto}
        className={cn(
          "sticky top-11 z-[5] flex w-full items-center gap-2 rounded-t-[11px] px-3 py-2 text-left",
          tono === "rosso" && "bg-red-50 hover:bg-red-100",
          tono === "ambra" && "bg-amber-50 hover:bg-amber-100",
          !tono && "bg-muted hover:bg-muted/70",
        )}
      >
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
            !aperto && "-rotate-90",
          )}
        />
        <h2
          className={cn(
            "min-w-0 flex-1 truncate text-xs font-bold",
            tono === "rosso" && "text-red-700",
            tono === "ambra" && "text-amber-800",
          )}
        >
          {titolo}
        </h2>
        <span className="shrink-0 text-xs font-bold tabular-nums text-muted-foreground">
          {/* Cercando: «12 su 57» — il totale resta visibile, altrimenti sembra
              che il lavoro sia diminuito (scelta di Enrico, 13-08). */}
          {ricerca ? `${trovate.length} su ${righe.length}` : righe.length}
        </span>
      </button>

      {aperto && (
        <>
          <p className="border-t px-3 pt-1.5 text-[11px] leading-snug text-muted-foreground">
            {nota}
          </p>
          <div className="flex flex-col gap-1.5 rounded-b-xl p-2">
            {trovate.map((r) => (
              <Carta key={r.id} row={r} dnd={dnd} motivo={perche(r, ricerca)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── drawer della giornata ───────────────────────────────────────────────────

/**
 * La giornata intera, aperta dal numero del giorno o da «+N altri» (idea di
 * Enrico, 13-08). È anche il modo in cui il mese recupera la mezza giornata che
 * non può mostrare.
 *
 * Cliccando un carico si passa al drawer della riga: uno chiude e l'altro apre,
 * così non si impilano due pannelli sullo stesso bordo.
 */
function DrawerGiorno({
  giorno,
  onChiudi,
  onApriRiga,
}: {
  giorno: CalendarioGiorno;
  onChiudi: () => void;
  onApriRiga: (id: string) => void;
}) {
  useChiudiConEsc(onChiudi);
  const sezioni = [
    { titolo: "Mattina", righe: giorno.mattina },
    { titolo: "Pomeriggio", righe: giorno.pomeriggio },
    { titolo: "Orario da definire", righe: giorno.senza_fascia },
    { titolo: "Usciti", righe: giorno.uscite },
  ];
  const totale = righeDelGiorno(giorno).length;

  return (
    <div className="fixed inset-0 top-14 z-40 flex justify-end" role="dialog">
      <div className="absolute inset-0 bg-black/30" onClick={onChiudi} aria-hidden />
      <div className="relative z-10 flex h-full w-full max-w-md flex-col overflow-y-auto border-l bg-background shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b px-4 py-3">
          <div>
            <h3 className="font-semibold">{cap(etichettaGiorno(giorno.data))}</h3>
            <p className="text-xs text-muted-foreground">
              {totale === 0 ? "nessun ritiro" : `${totale} ${totale === 1 ? "ritiro" : "ritiri"}`}
            </p>
          </div>
          <button type="button" onClick={onChiudi} className="rounded p-1 hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-col gap-4 p-4">
          {totale === 0 && (
            <p className="text-sm text-muted-foreground">
              Niente in programma. Trascina qui una riga dalla fascia per fissarla in questo giorno.
            </p>
          )}
          {sezioni
            .filter((s) => s.righe.length > 0)
            .map((s) => (
              <div key={s.titolo} className="flex flex-col gap-1.5">
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  {s.titolo} · {s.righe.length}
                </h4>
                {s.righe.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => onApriRiga(r.id)}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-md border border-l-[3px] bg-card p-2 text-left hover:bg-muted",
                      bordoStato(r),
                    )}
                  >
                    <Bandiera row={r} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{r.cliente_nome}</span>
                      <span className="block text-[11px] text-muted-foreground">
                        {r.partito_il ? "uscito" : STATO_LABEL[r.stato]}
                        {r.ndp_numero ? ` · NDP ${r.ndp_numero}` : ""}
                        {r.pallet_previsti != null
                          ? ` · ${Math.round(r.pallet_previsti)} pallet`
                          : ""}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

/** Esc chiude: su un pannello che copre la pagina è la via d'uscita che tutti
 *  provano per prima — e senza, una modale aperta blocca tutto il resto. */
function useChiudiConEsc(onChiudi: () => void) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onChiudi();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onChiudi]);
}

// ── modale ──────────────────────────────────────────────────────────────────

function Modale({
  titolo,
  testo,
  avviso,
  azioni,
  onChiudi,
}: {
  titolo: string;
  testo: string;
  avviso?: string;
  azioni: { testo: string; primario?: boolean; fai: () => void }[];
  onChiudi: () => void;
}) {
  useChiudiConEsc(onChiudi);
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onChiudi();
      }}
    >
      <div className="w-full max-w-md rounded-xl border bg-background p-5 shadow-xl">
        <h3 className="text-base font-semibold">{titolo}</h3>
        <p className="mt-2 text-sm text-muted-foreground">{testo}</p>
        {avviso && (
          <p className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
            {avviso}
          </p>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          {azioni.map((a) => (
            <button
              key={a.testo}
              type="button"
              onClick={() => {
                a.fai();
                onChiudi();
              }}
              className={cn(
                "rounded-lg border px-3 py-2 text-sm font-medium",
                a.primario
                  ? "border-brand-700 bg-brand-700 text-white hover:bg-brand-600"
                  : "hover:bg-muted",
              )}
            >
              {a.testo}
            </button>
          ))}
          <button
            type="button"
            onClick={onChiudi}
            className="rounded-lg border px-3 py-2 text-sm font-medium hover:bg-muted"
          >
            Annulla
          </button>
        </div>
      </div>
    </div>
  );
}
